import React, { forwardRef, useImperativeHandle, useState } from 'react';
import { FeaturesIcons, Icon } from './FeaturesIcons';
import { FEATURE_VIDEOS, FeatureVideo, YOUTUBE_ID_PATTERN } from './featureVideos';
import './FeatureVideoTheater.css';

export type FeatureVideoTheaterHandle = {
  play: (index?: number) => void;
};

type FeatureVideoTheaterProps = {
  className?: string;
  videos?: FeatureVideo[];
};

const FeatureVideoTheater = forwardRef<FeatureVideoTheaterHandle, FeatureVideoTheaterProps>(
  ({ className, videos = FEATURE_VIDEOS }, ref) => {
    const [selectedVideoIndex, setSelectedVideoIndex] = useState(0);
    const [videoPlaying, setVideoPlaying] = useState(false);
    const selectedVideo = videos[selectedVideoIndex] ?? videos[0];

    const selectVideo = (index: number, play = false) => {
      const video = videos[index];
      if (!video || !YOUTUBE_ID_PATTERN.test(video.id)) return;
      setSelectedVideoIndex(index);
      setVideoPlaying(play && YOUTUBE_ID_PATTERN.test(video.id));
    };

    const playSelectedVideo = () => {
      if (!selectedVideo || !YOUTUBE_ID_PATTERN.test(selectedVideo.id)) return;
      setVideoPlaying(true);
    };

    useImperativeHandle(ref, () => ({
      play: (index) => {
        if (typeof index === 'number') {
          selectVideo(index, true);
          return;
        }
        playSelectedVideo();
      },
    }), [selectedVideo.id]);

    return (
      <div className={`video-theater${className ? ` ${className}` : ''}`}>
        <FeaturesIcons />
        <div className="video-player-shell">
          <span className="video-player-label">AACore Booking video</span>
          <button
            className="video-poster-button"
            type="button"
            aria-label={`Play ${selectedVideo.title}`}
            hidden={videoPlaying}
            onClick={playSelectedVideo}
          >
            <img
              src={`https://i.ytimg.com/vi/${selectedVideo.id}/maxresdefault.jpg`}
              alt={`Video thumbnail for ${selectedVideo.title}`}
              loading="lazy"
            />
            <span className="video-play"><Icon name="play" />Play video</span>
          </button>
          <iframe
            title={selectedVideo.frameTitle}
            src={videoPlaying ? `https://www.youtube-nocookie.com/embed/${selectedVideo.id}?rel=0&modestbranding=1&playsinline=1&autoplay=1` : undefined}
            loading="lazy"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            hidden={!videoPlaying}
          />
        </div>
        <div className="video-program">
          <div className="video-current" aria-live="polite">
            <div className="video-kind"><Icon name="play" /><span>{selectedVideo.kind}</span></div>
            <h3>{selectedVideo.title}</h3>
            <p>{selectedVideo.description}</p>
            <a href={`https://www.youtube.com/shorts/${selectedVideo.id}`} target="_blank" rel="noopener noreferrer">
              Watch on YouTube
              <Icon name="arrow" />
            </a>
          </div>
          <div className="video-chapters" role="group" aria-label="Choose a feature video">
            {videos.map((video, index) => (
              <button
                key={video.id}
                className="video-chapter"
                type="button"
                aria-pressed={index === selectedVideoIndex}
                onClick={() => selectVideo(index)}
              >
                <span className="chapter-icon"><Icon name={video.icon} /></span>
                <span className="chapter-copy">
                  <strong>{video.chapterTitle}</strong>
                  <span className="chapter-label">{video.chapterLabel}</span>
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }
);

FeatureVideoTheater.displayName = 'FeatureVideoTheater';

export default FeatureVideoTheater;
